import { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconBrandDokobit } from '@components/icons/brand-dokobit';
import { IconBrandGetAccept } from '@components/icons/brand-getaccept';
import { IconBrandHireflix } from '@components/icons/brand-hireflix';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { IconBrandMyTimePlan } from '@components/icons/brand-mytimeplan';
import { IconBrandOneSystems } from '@components/icons/brand-onesystems';
import { IconBrandSignet } from '@components/icons/brand-signet';
import { IconBrandSlack } from '@components/icons/brand-slack';
import { IconBrandTaktikal } from '@components/icons/brand-taktikal';
import { IconBrandTimon } from '@components/icons/brand-timon';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Spinner } from '@components/loading-spinner';
import { PageContainers } from '@components/page-containers';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useIntegrationSettings } from 'api/companies/hooks';
import { PageContent } from 'components/Page';
import { useFeature } from 'features/authentication/authentication-queries';

import IntegrationHeader from '../../static/images/integrations-header.png';

const LINK_TO_MARKETPLACE = 'https://www.50skills.com/marketplace';

const MarketplaceItem = () => {
  return (
    <MarketplaceCard>
      <IconMarketplace color="purple100" size={40} />
    </MarketplaceCard>
  );
};

type IntegrationCardProps = {
  icon: React.ReactNode;
  label: string;
  href: string;
  info: string;
};

const IntegrationCard = (props: IntegrationCardProps) => {
  const { icon, label, href, info } = props;
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Card space={4}>
      <Stack orientation="horizontal" align="center" space={4} justify="space-between">
        <Stack orientation="horizontal" space={3}>
          {icon}
          <Stack orientation="vertical">
            <Text font="bold14" text={label} />
            <Text font="regular14" text={info} />
          </Stack>
        </Stack>
        <Button
          iconLeft={<IconAddPlus />}
          variant="ghost"
          onClick={() => navigate(href, { state: { from: location.pathname } })}
          text={t('add')}
        />
      </Stack>
    </Card>
  );
};

type GroupLink = {
  href: string;
  icon: ReactElement;
  label: string;
  info: string;
  connected: boolean;
};

const handleGoToMarketplace = () => {
  window.open(LINK_TO_MARKETPLACE, '_blank');
};

export const IntegrationItems = () => {
  const { t } = useScopedTranslate(locale);
  const hasJourneysIntegration = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  function getIntegrationLinks(): GroupLink[] {
    const iconSize = 40;
    const hrefPrefix = '/settings/apps';
    const integrationLinks = [
      {
        icon: <IconBrandJourneys size={iconSize} />,
        href: `${hrefPrefix}/journeys`,
        label: t('journeys'),
        info: t('journeysInfo'),
        connected: !hasJourneysIntegration || connectedIntegrations.includes('journeys'),
      },
      {
        icon: <IconBrandSlack size={iconSize} />,
        href: `${hrefPrefix}/slack`,
        label: t('slack'),
        info: t('slackInfo'),
        connected: connectedIntegrations.includes('slack'),
      },
      {
        icon: <IconBrandGetAccept size={iconSize} />,
        href: `${hrefPrefix}/getaccept`,
        label: t('getAccept'),
        info: t('eSigning'),
        connected: connectedIntegrations.includes('getaccept'),
      },
      {
        icon: <IconBrandHireflix size={iconSize} />,
        href: `${hrefPrefix}/hireflix`,
        label: t('hireflix'),
        info: t('videoInterview'),
        connected: connectedIntegrations.includes('hireflix'),
      },
      {
        icon: <IconBrandDokobit size={iconSize} />,
        href: `${hrefPrefix}/dokobit`,
        label: t('dokobit'),
        info: t('eSigning'),
        connected: connectedIntegrations.includes('dokobit'),
      },
      {
        icon: <IconBrandOneSystems size={iconSize} />,
        href: `${hrefPrefix}/onecrm`,
        label: t('oneSystems'),
        info: t('oneSystemsInfo'),
        connected: connectedIntegrations.includes('onecrm'),
      },
      {
        icon: <IconBrandTimon size={iconSize} />,
        href: `${hrefPrefix}/timon`,
        label: t('timon'),
        info: t('timonInfo'),
        connected: connectedIntegrations.includes('timon'),
      },
      {
        icon: <IconBrandMyTimePlan size={iconSize} />,
        href: `${hrefPrefix}/mytimeplan`,
        label: t('myTimeplan'),
        info: t('mytimeplanInfo'),
        connected: connectedIntegrations.includes('mytimeplan'),
      },
      {
        icon: <IconBrandTaktikal size={iconSize} />,
        href: `${hrefPrefix}/taktikal`,
        label: t('taktikal'),
        info: t('eSigning'),
        connected: connectedIntegrations.includes('taktikal'),
      },
      {
        icon: <IconBrandSignet size={iconSize} />,
        href: `${hrefPrefix}/signet`,
        label: t('signet'),
        info: t('eSigning'),
        connected: connectedIntegrations.includes('signet'),
      },
    ];
    return integrationLinks.filter((integration) => !integration.connected);
  }

  const integrationSettingsQuery = useIntegrationSettings();
  const connectedIntegrations = integrationSettingsQuery.data?.connected ?? [];

  if (integrationSettingsQuery.isLoading) {
    return <Spinner />;
  }

  const integrationGroup = getIntegrationLinks();

  return (
    <>
      <HeaderInfoContainer justify="center">
        <Stack orientation="horizontal" justify="space-between">
          <Stack space={10} align="center" orientation="horizontal">
            <MarketplaceItem />
            <Stack space={4}>
              <Stack orientation="vertical">
                <Text font="bold16" color="purple100" text={t('getApps')} />
                <Text color="purple100" text={t('help3')} />
              </Stack>
              <div>
                <Button
                  iconRight={<IconOpenNewTab />}
                  text={t('goToMarketplace')}
                  onClick={handleGoToMarketplace}
                />
              </div>
            </Stack>
          </Stack>
        </Stack>
      </HeaderInfoContainer>
      <PageContent>
        <PageContainers.MiddleColumnPage>
          <StyledContent space={6}>
            <Text text={t('helpText')} />
            <Separator />
            <Stack space={2}>
              {integrationGroup.map((integration) => (
                <IntegrationCard
                  info={integration.info}
                  key={integration.label}
                  icon={integration.icon}
                  href={integration.href}
                  label={integration.label}
                />
              ))}
            </Stack>
          </StyledContent>
        </PageContainers.MiddleColumnPage>
      </PageContent>
    </>
  );
};

const StyledContent = styled(Stack)({
  marginTop: pxToRem(16),
});

const MarketplaceCard = styled.div({
  height: pxToRem(80),
  width: pxToRem(80),
  backgroundColor: theme.color.white100,
  borderRadius: pxToRem(8),
  boxShadow: theme.shadow.fancy,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeaderInfoContainer = styled(Stack)({
  backgroundColor: theme.color.red20,
  padding: `${pxToRem(24)} ${pxToRem(40)} ${pxToRem(24)} ${pxToRem(40)}`,
  backgroundImage: `url(${IntegrationHeader})`,
  backgroundSize: pxToRem(345),
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right',
});

const locale = {
  en: {
    eSigning: 'Send documents to e-signing',
    slackInfo: 'Get notified in Slack on key events',
    videoInterview: 'Video interviews',
    timonInfo: 'Scheduling, absence and productivity',
    getApps: 'Get more apps',
    add: 'Add',
    hireflix: 'Hireflix',
    getAccept: 'GetAccept',
    onboardingForm: 'Onboarding Form',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrMonitor: 'HR Monitor',
    relesys: 'Relesys',
    h3core: 'H3 Core',
    dokobit: 'Dokobit',
    oneSystems: 'One Systems',
    oneSystemsInfo: 'CRM, Case management and scheduling',
    timon: 'Timon',
    myTimeplan: 'MyTimeplan',
    mytimeplanInfo: 'Complete workforce management software',
    taktikal: 'Taktikal',
    signet: 'Signet',
    slack: 'Slack',
    adManager: 'ADManager',
    help3: 'Looking for more ways to optimise your hiring?\nCheck out our marketplace',
    helpText:
      'Integrations that can be activated self service are presented here. If you require an integration to software that is not listed please contact us.',
    goToMarketplace: 'Go to marketplace',
    connected: 'Connected',
    journeys: 'Journeys',
    journeysInfo: 'Send applicants on journeys',
  },
  is: {
    eSigning: 'Sendu skjöl til rafrænnar undirskriftar',
    slackInfo: 'Fáðu tilkynningar í Slack um helstu atburði',
    videoInterview: 'Myndbandsviðtöl',
    timonInfo: 'Tímasetningar, fjarvera og framleiðni',
    getApps: 'Sækja fleiri forrit',
    add: 'Bæta við',
    hireflix: 'Hireflix',
    getAccept: 'GetAccept',
    onboardingForm: 'Innleiðingarform',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrMonitor: 'HR Monitor',
    relesys: 'Relesys',
    h3core: 'H3 Core',
    dokobit: 'Dokobit',
    oneSystems: 'One Systems',
    oneSystemsInfo: 'CRM, Málastjórnun og tímasetningar',
    timon: 'Timon',
    myTimeplan: 'MyTimeplan',
    mytimeplanInfo: 'Fullkominn hugbúnaður fyrir starfsmannastjórnun',
    taktikal: 'Taktikal',
    signet: 'Signet',
    slack: 'Slack',
    adManager: 'ADManager',
    help3:
      'Ertu að leita að fleiri leiðum til að hámarka ráðningar þínar?\nKíktu á markaðstorgið okkar',
    helpText:
      'Samþættingar sem hægt er að virkja eru listaðar upp hér. Ef þú ert að leita að samþættingu við hugbúnað sem ekki er skráður hér vinsamlegast hafðu samband við okkur.',
    goToMarketplace: 'Fara á markaðstorg',
    connected: 'Tengt',
    journeys: 'Journeys',
    journeysInfo: 'Sendu umsækjendur yfir í Journeys kerfið',
  },
};
